/**
 * Localized product gallery runtime
 *
 * Owns deferred image hydration, request priority, handleized variant filter
 * sync, the mobile Swiper adapter, and a picture-aware lightbox adapter. Native
 * <product-gallery> / <scroll-carousel> continue to own scrolling, controls,
 * thumbnails, media play/pause, and the public PhotoSwipe instance
 * (productGallery.lightBox).
 *
 * State: one object per <product-gallery> in a WeakMap. AbortController cleans
 * listeners; IntersectionObserver is disconnected on section unload.
 */

(function () {
  'use strict';

  var DESKTOP_QUERY = '(min-width: 1000px)';
  var GRID_ROOT_MARGIN = '600px 0px';
  var LEADING_EAGER_COUNT = 2;
  var galleryStates = new WeakMap();

  function handleize(value) {
    return String(value == null ? '' : value)
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-{2,}/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function downcase(value) {
    return String(value == null ? '' : value).trim().toLowerCase();
  }

  function resolveForm(productGallery) {
    var formId = productGallery.getAttribute('form');
    if (!formId) return null;
    return document.getElementById(formId) || document.forms[formId] || null;
  }

  function resolveCarousel(productGallery) {
    return (
      productGallery.querySelector('[data-product-gallery-carousel]') ||
      productGallery.querySelector('.product-gallery__carousel')
    );
  }

  // While mobile Swiper is active the cells live inside a .swiper-wrapper, so
  // this must not assume they stay direct children. Prefer the carousel's public
  // allCells contract (the adapter keeps it in original media-position order)
  // and fall back to a descendant query that matches either shape.
  function allMediaCells(carousel) {
    if (!carousel) return [];

    if (
      Object.prototype.hasOwnProperty.call(carousel, 'allCells') &&
      Array.isArray(carousel.allCells)
    ) {
      return carousel.allCells.slice();
    }

    return Array.prototype.slice.call(
      carousel.querySelectorAll(
        ':scope > .product-gallery__media, :scope > .swiper-wrapper > .product-gallery__media',
      ),
    );
  }

  function visibleMediaCells(carousel) {
    return allMediaCells(carousel).filter(function (cell) {
      return !cell.hasAttribute('hidden');
    });
  }

  function findCellByMediaId(carousel, mediaId) {
    if (mediaId == null || mediaId === '') return null;
    var needle = String(mediaId);
    var cells = allMediaCells(carousel);
    for (var i = 0; i < cells.length; i += 1) {
      if (cells[i].getAttribute('data-media-id') === needle) return cells[i];
    }
    return null;
  }

  function findCellByPosition(carousel, position) {
    if (position == null || position === '') return null;
    var needle = String(position);
    var cells = allMediaCells(carousel);
    for (var i = 0; i < cells.length; i += 1) {
      if (cells[i].getAttribute('data-media-position') === needle) return cells[i];
    }
    return null;
  }

  function findCellImage(cell) {
    if (!cell) return null;
    // The tiny server-rendered preview is only a visual placeholder. The
    // gallery's loading, priority, and lightbox logic must use the full image.
    var direct = cell.querySelector(':scope > img.product-gallery__media-frame');
    if (direct) return direct;
    var picture = cell.querySelector(':scope > picture');
    return picture ? picture.querySelector('img') : null;
  }

  function deferredTemplate(cell) {
    return cell ? cell.querySelector(':scope > template[data-product-gallery-deferred-image]') : null;
  }

  function findImageInFragment(root) {
    if (!root) return null;
    if (root.matches && root.matches('img')) return root;
    if (root.querySelector) {
      if (root.matches && root.matches('picture')) return root.querySelector('img');
      return root.querySelector('img');
    }
    return null;
  }

  function applyRequestAttributes(img, policy) {
    if (!img || !policy) return;

    var currentPriority = img.getAttribute('fetchpriority');
    var currentLoading = img.getAttribute('loading');

    if (policy.loading === 'eager') {
      img.setAttribute('loading', 'eager');
    } else if (policy.loading && currentLoading !== 'eager') {
      img.setAttribute('loading', policy.loading);
    }

    if (policy.fetchpriority === 'high') {
      img.setAttribute('fetchpriority', 'high');
    } else if (policy.fetchpriority === 'low' && currentPriority !== 'high') {
      img.setAttribute('fetchpriority', 'low');
    }
  }

  function bindCellLoadedState(cell) {
    if (!cell || cell.dataset.galleryLoadedBound === 'true') return;
    cell.dataset.galleryLoadedBound = 'true';

    if (cell.getAttribute('data-media-type') !== 'image') {
      cell.classList.add('is-loaded');
      return;
    }

    var img = findCellImage(cell);
    if (!img) {
      // Still deferred — bind again after hydration.
      cell.dataset.galleryLoadedBound = 'false';
      return;
    }

    // complete also covers failed cached requests whose error event already fired.
    if (img.complete) {
      cell.classList.add('is-loaded');
      return;
    }

    function done() {
      img.removeEventListener('load', done);
      img.removeEventListener('error', done);
      cell.classList.add('is-loaded');
    }

    img.addEventListener('load', done);
    img.addEventListener('error', done);
  }

  function hydrateImageCell(cell, policy) {
    if (!cell || cell.getAttribute('data-media-type') !== 'image') return null;

    var existing = findCellImage(cell);
    if (existing) {
      applyRequestAttributes(existing, policy);
      bindCellLoadedState(cell);
      return existing;
    }

    var template = deferredTemplate(cell);
    if (!template) return null;

    var fragment = template.content.cloneNode(true);
    var fragmentImage = findImageInFragment(fragment.firstElementChild) || findImageInFragment(fragment);
    applyRequestAttributes(fragmentImage, policy);
    template.replaceWith(fragment);
    bindCellLoadedState(cell);
    return findCellImage(cell);
  }

  function whenImageSettled(img) {
    return new Promise(function (resolve) {
      if (!img) {
        resolve(null);
        return;
      }
      if (img.complete) {
        resolve(img);
        return;
      }
      function done() {
        img.removeEventListener('load', done);
        img.removeEventListener('error', done);
        resolve(img);
      }
      img.addEventListener('load', done);
      img.addEventListener('error', done);
    });
  }

  function selectedMediaCell(carousel) {
    if (!carousel) return null;
    if (carousel.selectedCell) return carousel.selectedCell;
    return (
      carousel.querySelector('.product-gallery__media.is-initial:not([hidden])') ||
      carousel.querySelector('.product-gallery__media:not([hidden])')
    );
  }

  function nextVisibleImageCell(carousel, fromCell) {
    var visible = visibleMediaCells(carousel);
    var start = fromCell ? visible.indexOf(fromCell) : -1;
    for (var i = Math.max(start + 1, 0); i < visible.length; i += 1) {
      if (visible[i].getAttribute('data-media-type') === 'image') return visible[i];
    }
    return null;
  }

  function isDesktopGrid(productGallery, state) {
    var layout = productGallery.getAttribute('data-desktop-layout') || '';
    return !!(state.desktopMedia && state.desktopMedia.matches && layout.indexOf('grid') !== -1);
  }

  function promoteSelectedAndAdjacent(productGallery, state, selectedCell) {
    var carousel = state.carousel;
    if (!carousel) return;

    var selected = selectedCell || selectedMediaCell(carousel);
    if (!selected) return;

    state.priorityToken = (state.priorityToken || 0) + 1;
    var token = state.priorityToken;

    var selectedImage = null;
    if (selected.getAttribute('data-media-type') === 'image') {
      selectedImage = hydrateImageCell(selected, { loading: 'eager', fetchpriority: 'high' });
    }

    whenImageSettled(selectedImage).then(function () {
      var current = galleryStates.get(productGallery);
      if (!current || current !== state || current.priorityToken !== token) return;
      if (isDesktopGrid(productGallery, current)) return;

      var adjacent = nextVisibleImageCell(current.carousel, selected);
      if (adjacent) {
        hydrateImageCell(adjacent, { loading: 'eager', fetchpriority: 'low' });
      }
    });
  }

  function disconnectGridObserver(state) {
    if (state.gridObserver) {
      try {
        state.gridObserver.disconnect();
      } catch (err) {
        /* noop */
      }
      state.gridObserver = null;
    }
  }

  function observeGridHydration(productGallery, state) {
    disconnectGridObserver(state);
    if (!isDesktopGrid(productGallery, state)) return;
    if (typeof IntersectionObserver === 'undefined') return;

    var carousel = state.carousel;
    if (!carousel) return;

    state.gridObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var cell = entry.target;
          hydrateImageCell(cell, { loading: 'eager', fetchpriority: 'low' });
          if (state.gridObserver) state.gridObserver.unobserve(cell);
        });
      },
      { root: null, rootMargin: GRID_ROOT_MARGIN, threshold: 0 },
    );

    allMediaCells(carousel).forEach(function (cell) {
      if (cell.getAttribute('data-media-type') !== 'image') return;
      if (findCellImage(cell)) return;
      if (!deferredTemplate(cell)) return;
      state.gridObserver.observe(cell);
    });
  }

  function syncGridObserver(productGallery, state) {
    if (isDesktopGrid(productGallery, state)) {
      observeGridHydration(productGallery, state);
    } else {
      disconnectGridObserver(state);
    }
  }

  function hiddenIndexesForVariant(productGallery, carousel, variant) {
    if (productGallery.getAttribute('data-gallery-variant-filter') !== 'true') {
      return [];
    }

    var titleHandle = handleize(variant && variant.title);
    var titleDowncase = downcase(variant && variant.title);
    productGallery.setAttribute('data-variant-title-handle', titleHandle);
    productGallery.setAttribute('data-variant-title-downcase', titleDowncase);

    var hidden = [];
    allMediaCells(carousel).forEach(function (cell, index) {
      var altHandle = cell.getAttribute('data-media-alt-handle') || '';
      var altDowncase = cell.getAttribute('data-media-alt-downcase') || '';
      var visible =
        altHandle === '' || altHandle === titleHandle || altDowncase === titleDowncase;
      if (!visible) hidden.push(index);
    });
    return hidden;
  }

  function oneBasedPositionsFromZeroIndexes(indexes) {
    return (indexes || []).map(function (index) {
      return index + 1;
    });
  }

  // The first images of the visible variant load together, not one at a time as
  // each cell is promoted or scrolled near. Priority is left alone: the featured
  // cell is already promoted separately, and the rest keep the browser default.
  function hydrateLeadingCells(carousel, count) {
    visibleImageCells(carousel)
      .slice(0, count)
      .forEach(function (cell) {
        hydrateImageCell(cell, { loading: 'eager' });
      });
  }

  function applyVariantFilter(productGallery, state, variant) {
    var carousel = state.carousel;
    if (!carousel || !variant) return;

    var hiddenIndexes = hiddenIndexesForVariant(productGallery, carousel, variant);

    if (typeof carousel.filter === 'function') {
      carousel.filter(hiddenIndexes);
    } else {
      allMediaCells(carousel).forEach(function (cell, index) {
        cell.toggleAttribute('hidden', hiddenIndexes.indexOf(index) !== -1);
      });
    }

    productGallery.setAttribute(
      'filtered-indexes',
      JSON.stringify(oneBasedPositionsFromZeroIndexes(hiddenIndexes)),
    );

    var featured = variant.featured_media;
    if (featured && featured.id != null) {
      var featuredCell = findCellByMediaId(carousel, featured.id);
      if (featuredCell && featuredCell.getAttribute('data-media-type') === 'image') {
        hydrateImageCell(featuredCell, { loading: 'eager', fetchpriority: 'high' });
      }
    }

    hydrateLeadingCells(carousel, LEADING_EAGER_COUNT);
    syncGridObserver(productGallery, state);
  }

  function buildLightboxDataSource(carousel) {
    var images = [];
    visibleMediaCells(carousel).forEach(function (cell) {
      if (cell.getAttribute('data-media-type') !== 'image') return;
      var image = findCellImage(cell);
      if (!image) return;
      images.push(image);
    });

    return images.map(function (image) {
      return {
        thumbnailElement: image,
        src: image.src,
        srcset: image.srcset,
        msrc: image.currentSrc || image.src,
        width: parseInt(image.getAttribute('width'), 10),
        height: parseInt(image.getAttribute('height'), 10),
        alt: image.alt,
        thumbCropped: true,
      };
    });
  }

  function visibleImageCells(carousel) {
    return visibleMediaCells(carousel).filter(function (cell) {
      return cell.getAttribute('data-media-type') === 'image';
    });
  }

  function onLightboxOpen(productGallery, state, event) {
    event.stopImmediatePropagation();

    var carousel = state.carousel;
    if (!carousel) return;

    var imageCells = visibleImageCells(carousel);
    var selected = selectedMediaCell(carousel);

    imageCells.forEach(function (cell) {
      var isSelected = cell === selected;
      hydrateImageCell(cell, {
        loading: 'eager',
        fetchpriority: isSelected ? 'high' : 'low',
      });
    });

    var dataSource = buildLightboxDataSource(carousel);
    if (!dataSource.length) return;

    var index = event.detail && event.detail.index;
    if (typeof index !== 'number' || index < 0 || index >= dataSource.length) {
      var selectedImageCell =
        selected && selected.getAttribute('data-media-type') === 'image' ? selected : null;
      index = selectedImageCell ? imageCells.indexOf(selectedImageCell) : 0;
      if (index < 0) index = 0;
      if (index >= dataSource.length) index = 0;
    }

    var lightBox = productGallery.lightBox;
    if (!lightBox || typeof lightBox.loadAndOpen !== 'function') return;
    lightBox.loadAndOpen(index, dataSource);
  }

  /**
   * -------------------------------------------------------------------------------------------------------------------
   * MOBILE SWIPER
   * -------------------------------------------------------------------------------------------------------------------
   * Below 1000px the one existing cell list is moved (never cloned) into a
   * .swiper-wrapper inside this same <scroll-carousel>, and Swiper drives it with
   * rewind: true / loop: false. A reversible set of own properties on this one
   * carousel keeps the public ScrollCarousel contract intact, so native
   * <product-gallery>, dots, thumbnails, and arrows keep working untouched.
   *
   * Hidden (variant-filtered) cells keep their hidden attribute and additionally
   * lose .swiper-slide, so Swiper never counts them: no blank slides, and the last
   * visible cell is a real end for rewind.
   */

  function swiperConstructor() {
    return typeof window.Swiper === 'function' ? window.Swiper : null;
  }

  function visibleFrom(cells) {
    return (cells || []).filter(function (cell) {
      return !cell.hasAttribute('hidden');
    });
  }

  function byMediaPosition(cells) {
    return cells.slice().sort(function (a, b) {
      var left = parseInt(a.getAttribute('data-media-position'), 10) || 0;
      var right = parseInt(b.getAttribute('data-media-position'), 10) || 0;
      return left - right;
    });
  }

  function emitCarousel(carousel, type, detail) {
    carousel.dispatchEvent(new CustomEvent(type, { detail: detail }));
  }

  // Swiper can report a negative activeIndex while its layout is still degenerate
  // (for example before a resize settles). selectedIndex feeds native dots and
  // thumbnails, which treat -1 as "nothing current", so clamp into visible range.
  function activeVisibleIndex(state) {
    if (!state.swiper) return 0;

    var index = state.swiper.activeIndex;
    if (typeof index !== 'number' || !isFinite(index) || index < 0) return 0;

    var lastIndex = visibleFrom(state.stableCells).length - 1;
    if (lastIndex < 0) return 0;
    return Math.min(index, lastIndex);
  }

  function syncSlideClasses(state) {
    (state.stableCells || []).forEach(function (cell) {
      var hidden = cell.hasAttribute('hidden');
      cell.classList.toggle('swiper-slide', !hidden);
      if (hidden) {
        cell.classList.remove('swiper-slide-active', 'swiper-slide-prev', 'swiper-slide-next');
      }
    });
  }

  function nativeScrolledMediaCell(carousel, cells) {
    if (!carousel || Math.abs(carousel.scrollLeft) < 1) return null;

    var visible = visibleFrom(cells);
    var carouselRect = carousel.getBoundingClientRect();
    var center = carouselRect.left + carouselRect.width / 2;
    var nearest = null;
    var distance = Infinity;

    visible.forEach(function (cell) {
      var rect = cell.getBoundingClientRect();
      var delta = Math.abs(rect.left + rect.width / 2 - center);
      if (delta < distance) {
        nearest = cell;
        distance = delta;
      }
    });

    return nearest;
  }

  /**
   * Swiper to native event bridge. Exactly one carousel:change per real selection
   * change, and exactly one matching carousel:settle.
   */
  function bridgeChange(state) {
    if (!state.swiper) return;
    var visible = visibleFrom(state.stableCells);
    var index = activeVisibleIndex(state);
    var cell = visible[index] || null;
    if (!cell || cell === state.bridgedCell) return;

    state.bridgedCell = cell;
    state.pendingSettle = true;
    emitCarousel(state.carousel, 'carousel:change', { index: index, cell: cell });
  }

  function bridgeSettle(state) {
    if (!state.swiper || !state.pendingSettle) return;
    state.pendingSettle = false;

    var visible = visibleFrom(state.stableCells);
    var index = activeVisibleIndex(state);
    emitCarousel(state.carousel, 'carousel:settle', {
      index: index,
      cell: visible[index] || null,
    });
  }

  // Programmatic and zero-duration moves do not always produce Swiper's normal
  // transition callbacks, so complete the pair here when nothing is animating.
  function flushBridge(state) {
    bridgeChange(state);
    if (state.pendingSettle && state.swiper && !state.swiper.animating) {
      bridgeSettle(state);
    }
  }

  function resolveSpeed(options) {
    // undefined lets Swiper fall back to params.speed.
    return options && options.instant ? 0 : undefined;
  }

  function swiperSelect(state, index, options) {
    if (!state.swiper) return;

    var visible = visibleFrom(state.stableCells);
    if (index == null || index < 0 || index >= visible.length) return;

    emitCarousel(state.carousel, 'carousel:select', { index: index, cell: visible[index] });
    state.swiper.slideTo(index, resolveSpeed(options));
    flushBridge(state);
  }

  function swiperFilter(state, indexes) {
    var hidden = indexes || [];
    var previousCell = visibleFrom(state.stableCells)[activeVisibleIndex(state)] || null;

    (state.stableCells || []).forEach(function (cell, index) {
      cell.toggleAttribute('hidden', hidden.indexOf(index) !== -1);
    });
    syncSlideClasses(state);

    if (state.swiper) {
      state.swiper.update();

      var visible = visibleFrom(state.stableCells);
      if (visible.length) {
        var target = visible.indexOf(previousCell);
        if (target < 0) target = 0;
        state.swiper.slideTo(target, 0);
        // The previous selection may have been filtered out; re-announce it.
        state.bridgedCell = null;
        flushBridge(state);
      }
    }

    emitCarousel(state.carousel, 'carousel:filter', { filteredIndexes: hidden });
  }

  /**
   * Install the reversible own-property adapter on this carousel only. Every
   * replaced descriptor is saved so teardown restores the prototype behavior.
   */
  function adaptCarousel(state) {
    var carousel = state.carousel;

    var descriptors = {
      allCells: {
        get: function () {
          return (state.stableCells || []).slice();
        },
      },
      cells: {
        get: function () {
          return visibleFrom(state.stableCells);
        },
      },
      selectedIndex: {
        get: function () {
          return activeVisibleIndex(state);
        },
      },
      selectedCell: {
        get: function () {
          return visibleFrom(state.stableCells)[activeVisibleIndex(state)] || null;
        },
      },
      isScrollable: {
        get: function () {
          return visibleFrom(state.stableCells).length > 1;
        },
      },
      select: {
        value: function (index, options) {
          swiperSelect(state, index, options || {});
        },
      },
      previous: {
        value: function (options) {
          if (!state.swiper) return;
          state.swiper.slidePrev(resolveSpeed(options));
          flushBridge(state);
        },
      },
      next: {
        value: function (options) {
          if (!state.swiper) return;
          // Swiper's built-in rewind returns to the first slide past the last one.
          state.swiper.slideNext(resolveSpeed(options));
          flushBridge(state);
        },
      },
      filter: {
        value: function (indexes) {
          swiperFilter(state, indexes || []);
        },
      },
    };

    var saved = {};
    Object.keys(descriptors).forEach(function (name) {
      saved[name] = Object.getOwnPropertyDescriptor(carousel, name) || null;

      var descriptor = descriptors[name];
      descriptor.configurable = true;
      if (Object.prototype.hasOwnProperty.call(descriptor, 'value')) {
        descriptor.writable = true;
      }
      Object.defineProperty(carousel, name, descriptor);
    });
    state.adapterSaved = saved;
  }

  function restoreAdapter(state) {
    var carousel = state.carousel;
    var saved = state.adapterSaved;
    if (!saved) return;

    Object.keys(saved).forEach(function (name) {
      try {
        delete carousel[name];
        if (saved[name]) Object.defineProperty(carousel, name, saved[name]);
      } catch (err) {
        /* noop */
      }
    });
    state.adapterSaved = null;
  }

  function enterMobileSwiper(state) {
    var carousel = state.carousel;
    var SwiperConstructor = swiperConstructor();
    if (!carousel || state.swiper || !SwiperConstructor) return;

    var cells = byMediaPosition(allMediaCells(carousel));
    if (!cells.length) return;

    // A user can scroll the native gallery before the deferred gallery scripts
    // arrive. Preserve the photo in view when Swiper takes over.
    var selected = nativeScrolledMediaCell(carousel, cells) || selectedMediaCell(carousel);

    var wrapper = document.createElement('div');
    wrapper.className = 'swiper-wrapper';
    cells.forEach(function (cell) {
      wrapper.appendChild(cell);
    });
    carousel.appendChild(wrapper);

    state.stableCells = cells;
    state.swiperWrapper = wrapper;

    // The native allCells getter honors cell-selector, so any native code path
    // reading it still finds the cells now that they are nested.
    state.hadCellSelector = carousel.hasAttribute('cell-selector');
    if (!state.hadCellSelector) {
      carousel.setAttribute('cell-selector', '.product-gallery__media');
    }

    carousel.classList.add('swiper', 'product-gallery__carousel--swiper');
    syncSlideClasses(state);

    var visible = visibleFrom(cells);
    var initialIndex = Math.max(visible.indexOf(selected), 0);

    adaptCarousel(state);

    state.swiper = new SwiperConstructor(carousel, {
      autoHeight: false,
      initialSlide: initialIndex,
      loop: false,
      rewind: true,
      slidesPerView: 1,
      slidesPerGroup: 1,
      spaceBetween: 0,
      navigation: { enabled: false },
      pagination: { enabled: false },
      watchOverflow: true,
      on: {
        slideChange: function () {
          bridgeChange(state);
        },
        transitionEnd: function () {
          bridgeSettle(state);
        },
      },
    });

    // One settle after setup so media, controls, and height state agree. When the
    // initial slide is not the first cell (a variant's featured media, say), also
    // announce the change, otherwise native dots and thumbnails stay on index 0.
    state.bridgedCell = visible[initialIndex] || null;
    if (initialIndex > 0 && state.bridgedCell) {
      emitCarousel(carousel, 'carousel:change', {
        index: initialIndex,
        cell: state.bridgedCell,
      });
    }
    state.pendingSettle = true;
    bridgeSettle(state);
  }

  function exitMobileSwiper(state) {
    var carousel = state.carousel;
    if (!carousel || !state.swiper) return;

    var selected = visibleFrom(state.stableCells)[activeVisibleIndex(state)] || null;
    var cells = state.stableCells || [];

    try {
      state.swiper.destroy(true, true);
    } catch (err) {
      /* noop */
    }
    state.swiper = null;
    restoreAdapter(state);

    // Move the exact original nodes back, in original media-position order.
    cells.forEach(function (cell) {
      cell.classList.remove('swiper-slide', 'swiper-slide-active', 'swiper-slide-next', 'swiper-slide-prev', 'swiper-slide-visible');
      cell.style.removeProperty('width');
      cell.style.removeProperty('height');
      cell.style.removeProperty('margin-right');
      cell.style.removeProperty('transform');
      if (!cell.getAttribute('style')) cell.removeAttribute('style');
      carousel.appendChild(cell);
    });

    if (state.swiperWrapper && state.swiperWrapper.parentNode === carousel) {
      carousel.removeChild(state.swiperWrapper);
    }

    carousel.classList.remove(
      'swiper',
      'product-gallery__carousel--swiper',
      'swiper-initialized',
      'swiper-horizontal',
      'swiper-vertical',
      'swiper-backface-hidden',
      'swiper-android',
      'swiper-ios',
    );
    if (!state.hadCellSelector) carousel.removeAttribute('cell-selector');
    carousel.style.removeProperty('cursor');

    state.swiperWrapper = null;
    state.stableCells = null;
    state.bridgedCell = null;
    state.pendingSettle = false;
    state.hadCellSelector = false;

    // Restore the equivalent visible selection to the native carousel.
    if (selected && typeof carousel.select === 'function') {
      var index = visibleFrom(allMediaCells(carousel)).indexOf(selected);
      if (index >= 0) carousel.select(index, { instant: true });
    }
  }

  function syncMobileSwiper(state) {
    if (!state.carousel) return;
    if (state.desktopMedia.matches) {
      exitMobileSwiper(state);
    } else {
      enterMobileSwiper(state);
    }
  }

  function bindGallery(productGallery) {
    if (galleryStates.has(productGallery)) return;

    var carousel = resolveCarousel(productGallery);
    if (!carousel) return;

    var abortController = new AbortController();
    var signal = abortController.signal;
    var state = {
      abortController: abortController,
      carousel: carousel,
      desktopMedia: window.matchMedia(DESKTOP_QUERY),
      form: resolveForm(productGallery),
      gridObserver: null,
      priorityToken: 0,
      adapterSaved: null,
      bridgedCell: null,
      hadCellSelector: false,
      pendingSettle: false,
      stableCells: null,
      swiper: null,
      swiperWrapper: null,
    };
    galleryStates.set(productGallery, state);

    allMediaCells(carousel).forEach(bindCellLoadedState);

    promoteSelectedAndAdjacent(productGallery, state);
    syncGridObserver(productGallery, state);
    syncMobileSwiper(state);

    carousel.addEventListener(
      'carousel:change',
      function (event) {
        var current = galleryStates.get(productGallery);
        if (!current) return;
        var cell = (event.detail && event.detail.cell) || selectedMediaCell(current.carousel);
        promoteSelectedAndAdjacent(productGallery, current, cell);
      },
      { signal: signal },
    );

    if (state.form) {
      state.form.addEventListener(
        'variant:change',
        function (event) {
          var current = galleryStates.get(productGallery);
          if (!current) return;
          applyVariantFilter(productGallery, current, event.detail && event.detail.variant);
        },
        { capture: true, signal: signal },
      );
    }

    productGallery.addEventListener(
      'lightbox:open',
      function (event) {
        var current = galleryStates.get(productGallery);
        if (!current) return;
        onLightboxOpen(productGallery, current, event);
      },
      { capture: true, signal: signal },
    );

    var onViewportChange = function () {
      var current = galleryStates.get(productGallery);
      if (!current) return;
      syncMobileSwiper(current);
      syncGridObserver(productGallery, current);
      if (!isDesktopGrid(productGallery, current)) {
        promoteSelectedAndAdjacent(productGallery, current);
      }
    };

    if (typeof state.desktopMedia.addEventListener === 'function') {
      state.desktopMedia.addEventListener('change', onViewportChange, { signal: signal });
    } else if (typeof state.desktopMedia.addListener === 'function') {
      state.desktopMedia.addListener(onViewportChange);
      signal.addEventListener('abort', function () {
        state.desktopMedia.removeListener(onViewportChange);
      });
    }
  }

  function teardownGallery(productGallery) {
    var state = galleryStates.get(productGallery);
    if (!state) return;

    exitMobileSwiper(state);
    disconnectGridObserver(state);
    if (state.abortController) {
      try {
        state.abortController.abort();
      } catch (err) {
        /* noop */
      }
    }
    galleryStates.delete(productGallery);
  }

  function getProductGalleries(root) {
    var scope = root || document;
    if (!scope.querySelectorAll) return [];
    if (scope.matches && scope.matches('product-gallery[form][data-desktop-layout]')) {
      return [scope];
    }
    if (scope.matches && scope.matches('product-gallery[form]')) {
      return [scope];
    }
    return Array.prototype.slice.call(scope.querySelectorAll('product-gallery[form]'));
  }

  function bootstrap(root) {
    getProductGalleries(root).forEach(bindGallery);
  }

  function bindQuickBuyLoadedState(modal) {
    getProductGalleries(modal).forEach(function (gallery) {
      // Quick Buy uses the native gallery: bind readiness without promoting
      // image requests or attaching the product-page Swiper/control adapters.
      allMediaCells(resolveCarousel(gallery)).forEach(bindCellLoadedState);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      bootstrap(document);
    });
  } else {
    bootstrap(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    bootstrap(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    getProductGalleries(event.target).forEach(teardownGallery);
  });

  document.addEventListener('dialog:before-show', function (event) {
    if (event.target.matches('quick-buy-modal')) bindQuickBuyLoadedState(event.target);
  }, true);

  // Product changes can replace the gallery without a Shopify section-load event.
  // Capture runs before <product-rerender> replaces its subtree at the form.
  document.addEventListener('product:rerender', function (event) {
    var quickBuyModal = event.target.closest('quick-buy-modal');
    if (quickBuyModal) {
      // Quick Buy replaces its entire subtree even for ordinary variant changes.
      Promise.resolve().then(function () {
        if (quickBuyModal.isConnected) bindQuickBuyLoadedState(quickBuyModal);
      });
      return;
    }
    if (!event.detail || !event.detail.productChange) return;
    var section = event.target.closest('.shopify-section');
    if (!section) return;
    var previousGalleries = getProductGalleries(section);

    Promise.resolve().then(function () {
      previousGalleries.forEach(function (gallery) {
        if (!gallery.isConnected) teardownGallery(gallery);
      });
      if (section.isConnected) bootstrap(section);
    });
  }, true);
})();
